import type { ComponentType, ReactElement } from 'react';
import { sortPackages } from 'packages/common/sorters/sortPackages';
import type { Package } from 'packages/types';

// Row props travel as data so `Package` can stay a stable module-level
// reference — it is a JSX element type, and a fresh identity per render
// remounts every row, losing `PackageContainer`'s accordion state. RowProps
// is generic, not one fixed shape, because each list's row needs a
// different, sometimes-required slice of it (e.g. only the venue and
// meeting rows require `allServices`); tying `Package` and `packageProps`
// to the same type parameter lets each call site prove its own row's
// requirements.
type Props<Pkg, RowProps extends object = object> = {
  packages: Pkg[];
  Package: ComponentType<{ pkg: Pkg } & RowProps>;
  packageProps?: RowProps;
};

export function PackageList<
  Pkg extends Package,
  RowProps extends object = object
>(props: Props<Pkg, RowProps>): ReactElement {
  return (
    <>
      {sortPackages(props.packages).map(pkg => (
        // TS can't verify a generic spread reconstructs `{ pkg: Pkg } &
        // RowProps` from `RowProps` alone — true for every instantiation
        // the signature above allows, just not provable inside a generic
        // body.
        <props.Package
          key={pkg.id}
          {...(props.packageProps as RowProps)}
          pkg={pkg}
        />
      ))}
    </>
  );
}
